import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Eye, EyeOff, Copy, RefreshCw, Trash2, Key } from "lucide-react";
import { toast } from "sonner";
import { useApiToken, useGenerateApiToken, useRevokeApiToken } from "@/hooks/use-api-token";

interface APITokenSectionProps {
  organizationId: string;
}

export const APITokenSection = ({ organizationId }: APITokenSectionProps) => {
  const [showToken, setShowToken] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const { data: tokenInfo, isLoading, refetch } = useApiToken(organizationId);
  const generateApiToken = useGenerateApiToken();
  const revokeApiToken = useRevokeApiToken();

  const handleGenerateToken = async () => {
    setIsGenerating(true);
    try {
      const result = await generateApiToken.mutateAsync({ organizationId });
      if (result.success) {
        toast.success("API token generated successfully");
        refetch();
      }
    } catch (error) {
      toast.error("Failed to generate API token");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeToken = async () => {
    setIsRevoking(true);
    try {
      const result = await revokeApiToken.mutateAsync({ organizationId });
      if (result.success) {
        toast.success("API token revoked successfully");
        refetch();
      }
    } catch (error) {
      toast.error("Failed to revoke API token");
    } finally {
      setIsRevoking(false);
    }
  };

  const handleCopyToken = () => {
    if (tokenInfo?.token) {
      navigator.clipboard.writeText(tokenInfo.token);
      toast.success("API token copied to clipboard");
    }
  };

  const handleCopyOrgId = () => {
    navigator.clipboard.writeText(organizationId);
    toast.success("Organization ID copied to clipboard");
  };

  const maskToken = (token: string) => {
    if (!token) return "";
    const visibleChars = 8;
    if (token.length <= visibleChars) return token;
    return token.substring(0, visibleChars) + "•".repeat(token.length - visibleChars);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-[#DFEF97] text-black border-[#DFEF97] hover:bg-[#DFEF97]/90">Active</Badge>;
      case 'revoked':
        return <Badge className="bg-red-500 text-white border-red-500 hover:bg-red-500/90">Revoked</Badge>;
      case 'inactive':
      default:
        return <Badge className="bg-red-500 text-white border-red-500 hover:bg-red-500/90">Inactive</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Token Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Production API Access
        </CardTitle>
        <CardDescription>
          Use these credentials to make outbound REST API calls that fetch live
          device data and updates from Snapshot. Pair your Organization ID with
          the API Token in the <code className="bg-muted px-1 rounded">Authorization</code> header.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Organization ID */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Organization ID</Label>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={organizationId}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyOrgId}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Use this ID along with your API token for API requests.
          </p>
        </div>

        {/* Token Status */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Token Status</Label>
            <div className="flex items-center gap-2">
              {getStatusBadge(tokenInfo?.status || 'inactive')}
              {tokenInfo?.createdAt && (
                <span className="text-sm text-muted-foreground">
                  Created: {new Date(tokenInfo.createdAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Token Display */}
        {tokenInfo?.hasToken && tokenInfo?.status === 'active' && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">API Token</Label>
            <div className="flex items-center gap-2">
              <Input
                type={showToken ? "text" : "password"}
                value={showToken ? (tokenInfo?.token || "") : maskToken(tokenInfo?.token || "")}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyToken}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Keep this token secure. It provides access to your organization's API endpoints.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-4">
          {!tokenInfo?.hasToken || tokenInfo?.status !== 'active' ? (
            <Button
              onClick={handleGenerateToken}
              disabled={isGenerating}
              className="flex items-center gap-2"
            >
              {isGenerating ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Key className="h-4 w-4" />
              )}
              Generate API Token
            </Button>
          ) : (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Regenerate Token
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Regenerate API Token</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will create a new API token and invalidate the current one. 
                      Any applications using the current token will need to be updated.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleGenerateToken} disabled={isGenerating}>
                      {isGenerating ? "Generating..." : "Regenerate"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Revoke Token
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revoke API Token</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently revoke your API token. Any applications using 
                      this token will stop working immediately. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleRevokeToken} 
                      disabled={isRevoking}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isRevoking ? "Revoking..." : "Revoke Token"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>

        {/* Usage Info */}
        {tokenInfo?.lastUsedAt && (
          <div className="text-sm text-muted-foreground pt-2 border-t">
            Last used: {new Date(tokenInfo.lastUsedAt).toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};